// src/pages/NotFound.jsx
//
// Catch-all 404 page rendered by `<Route path="*" element={<NotFound />} />`
// in App.jsx. Lives OUTSIDE the AppShell — this is what the user sees
// when a typo'd URL or a stale link bounces them past every other
// matched route, so the layout has to be standalone (no sidebar, no
// AppShell context) and degrade gracefully if router context is
// unexpectedly missing.
//
// Design language: lime / pitch system shared with the rest of the
// app (#b5f03d lime, #0d1f14 pitch, #5a8a1f moss, #f7faf5 light bg,
// Barlow Condensed for headings).

import React from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowLeftIcon,
  LifebuoyIcon,
  MapIcon,
} from '@heroicons/react/24/outline'

const HEAD_FONT = "'Barlow Condensed', sans-serif"

/**
 * Internal anchor that prefers react-router's <Link> but falls back
 * to a plain <a> if Router context is somehow unavailable. Belt-and-
 * braces against the rare-but-possible case where NotFound is
 * rendered outside a <Routes> tree (Storybook, error boundaries, an
 * accidental top-level mount during boot, etc.) — react-router will
 * throw "useHref() may be used only in the context of a <Router>"
 * and the user lands on a blank screen instead of the friendly 404.
 */
function SafeLink({ to, children, className }) {
  try {
    return (
      <Link to={to} className={className}>
        {children}
      </Link>
    )
  } catch {
    return (
      <a href={to} className={className}>
        {children}
      </a>
    )
  }
}

export default function NotFound() {
  return (
    <div
      className="min-h-svh flex flex-col items-center justify-center px-6 py-12 text-center bg-[#f7faf5] text-[#0d1f14]"
      style={{
        // Subtle pitch grid wash so the page reads as part of the app,
        // not a generic browser error screen.
        backgroundImage:
          'linear-gradient(rgba(13,31,20,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(13,31,20,0.04) 1px, transparent 1px)',
        backgroundSize: '32px 32px',
      }}
    >
      <div className="w-full max-w-md flex flex-col items-center">
        {/* Pitch tile with the lime icon — same visual language as
            the BetaFeedbackButton launcher. */}
        <div
          className="mb-6 inline-flex items-center justify-center h-16 w-16 rounded-2xl bg-[#0d1f14] text-[#b5f03d] shadow-[0_4px_16px_0_rgba(13,31,20,0.25)] ring-2 ring-[#b5f03d]/40"
          aria-hidden="true"
        >
          <MapIcon className="h-8 w-8" />
        </div>

        <p
          className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#5a8a1f] mb-2"
          style={{ fontFamily: HEAD_FONT }}
        >
          404 &middot; Off the field
        </p>

        <h1
          className="text-4xl sm:text-5xl font-bold uppercase tracking-tight text-[#0d1f14] mb-3"
          style={{ fontFamily: HEAD_FONT, letterSpacing: '-0.5px' }}
        >
          We couldn&apos;t find that page.
        </h1>

        <p className="text-base text-zinc-600 mb-8 max-w-sm">
          The link may be old, mistyped, or the page might have moved.
          Let&apos;s get you back on the pitch.
        </p>

        <div className="flex flex-col sm:flex-row gap-2.5 w-full sm:w-auto">
          <SafeLink
            to="/dashboard"
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-5 py-3 text-sm font-bold uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-all active:scale-[0.97]"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            <span style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}>
              Back to home
            </span>
          </SafeLink>
          <SafeLink
            to="/support"
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-zinc-200 bg-white hover:border-[#5a8a1f]/40 hover:bg-[#f7faf5] text-[#0d1f14] px-5 py-3 text-sm font-bold uppercase transition-all"
          >
            <LifebuoyIcon className="h-4 w-4 text-[#5a8a1f]" />
            <span style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}>
              Help
            </span>
          </SafeLink>
        </div>
      </div>
    </div>
  )
}
